import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Users, Target, Landmark, Layers, Handshake, AlertTriangle, Rocket,
  Award, Compass, Scale, Globe, Eye,
  TrendingUp, Building2, Database, Sparkles, ShieldCheck, LineChart, Route,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import StrategyNetwork from "@/components/heroes/TransformationJourney";

const FAILURES = [
  { icon: Users, title: "Lack of executive ownership", body: "Transformation stalls when leadership treats it as a technology programme rather than a business one, and no single senior voice owns the outcome." },
  { icon: Target, title: "Unclear business objectives", body: "Without a defensible answer to what the business is trying to achieve, technology decisions drift and investment loses its line to value." },
  { icon: Landmark, title: "Poor governance", body: "Decisions are made in isolation, risks are managed after the fact, and the board loses a clear line of sight on what is actually happening." },
  { icon: Layers, title: "Disconnected departments", body: "Functions optimise for their own metrics while the business as a whole moves more slowly, more expensively and with more friction." },
  { icon: Handshake, title: "Vendor-driven decisions", body: "The technology agenda ends up shaped by suppliers rather than by the business itself, and choices are hard to defend later." },
  { icon: AlertTriangle, title: "Resistance to change", body: "People and processes are asked to move faster than the leadership team has prepared them to, and the change never takes hold." },
  { icon: Rocket, title: "Technology-first thinking", body: "The organisation begins with the platform or product it wants to introduce, and only afterwards asks what problem it was meant to solve." },
];

const TRANSFORMATION_OUTCOMES = [
  { title: "Operational Excellence", note: "A leaner, faster, more resilient core." },
  { title: "Business Growth", note: "New capacity, markets and revenue lines." },
  { title: "Improved Customer Experience", note: "A sharper, more consistent front line." },
  { title: "Better Decision Making", note: "The clarity to decide with confidence." },
  { title: "Scalable Operations", note: "Systems built to carry the next stage." },
  { title: "Business Agility", note: "The ability to move when the market does." },
  { title: "Technology Governance", note: "Oversight that stays with leadership." },
  { title: "Executive Visibility", note: "A clear board-level view of value." },
  { title: "Reduced Risk", note: "Cyber, delivery and vendor risk controlled." },
  { title: "Competitive Advantage", note: "Capability rivals cannot easily copy." },
];

const FRAMEWORK = [
  { title: "Business vision", body: "Every engagement begins with the destination: what the business is trying to become over the next five to ten years, and why that matters commercially." },
  { title: "Executive alignment", body: "The leadership team lines up behind a single direction. Without alignment at the top, no transformation programme survives its second quarter." },
  { title: "Current state assessment", body: "A candid, unblinking read on where the business really is: capability, cost, risk and the maturity of the operating core." },
  { title: "Business priorities", body: "The two or three moves that will most shape the next stage of the business are identified, sequenced and defended." },
  { title: "Technology roadmap", body: "A prioritised, board-ready plan that turns strategy into technology decisions the business can execute and stand behind." },
  { title: "Execution governance", body: "Delivery, teams and vendors are held to account against the business outcome, not the project plan." },
  { title: "Business outcomes", body: "Results are judged in the board's own terms: growth, margin, resilience and risk reduced." },
];

const CHALLENGES = [
  { title: "Rapid business growth", body: "Ambition is outpacing the systems and leadership meant to support it." },
  { title: "Legacy business processes", body: "The way work gets done was built for a different scale of business." },
  { title: "ERP modernisation", body: "The core platform decision is too large, and too costly, to get wrong." },
  { title: "AI readiness", body: "Leadership needs a clear, commercial view of where AI actually pays." },
  { title: "Technology governance", body: "Major decisions need the discipline and accountability the stakes require." },
  { title: "Operational inefficiency", body: "Cost and effort keep rising while value to the business barely moves." },
  { title: "Business scalability", body: "The organisation is running into ceilings that leadership must lift with intent." },
  { title: "Executive reporting", body: "The board and executive team cannot see the business as clearly as they must." },
  { title: "Technology investment decisions", body: "Spend keeps rising while its link to business return stays unclear." },
  { title: "Preparing for regional expansion", body: "New markets place fresh weight on governance, operating model and capacity." },
];

const CONVICTIONS = [
  { title: "Technology should support leadership", body: "The role of technology is to make it easier for a leadership team to lead the business, not harder." },
  { title: "Technology should simplify the business", body: "The best transformation decisions take complexity out of the operating core. They rarely add to it." },
  { title: "Technology should improve decision-making", body: "Systems and data earn their place when they help the executive team decide faster, and better." },
  { title: "Technology should increase business value", body: "Every investment should be judged against a business result the board can point to." },
  { title: "Transformation succeeds when leaders own the vision", body: "Programmes that are owned by leadership finish. Programmes that are delegated do not." },
];

const WHY = [
  { icon: Target, title: "Business-first perspective", body: "Every recommendation begins with the business result it is meant to create. Technology follows the strategy, never the other way around." },
  { icon: Award, title: "27+ years executive leadership", body: "Judgement built in real boardrooms, on real programmes, with real commercial consequence, not theory." },
  { icon: Scale, title: "Independent executive advice", body: "No products to sell and no platforms to push. The only interest is the decision that is right for your business." },
  { icon: Users, title: "Fractional CIO leadership", body: "Senior, board-level technology leadership on the cadence your business requires, without a permanent appointment." },
  { icon: Handshake, title: "Vendor-neutral recommendations", body: "Objective evaluation of partners, platforms and providers, free of commercial bias or hidden incentives." },
  { icon: Compass, title: "Practical transformation strategy", body: "A grounded plan the business can genuinely execute, not a document that looks impressive and moves nothing." },
  { icon: Globe, title: "Experience across India, the UAE and the Gulf", body: "Direct experience of business conditions across the region, and how transformation decisions play out in practice." },
  { icon: Eye, title: "Long-term strategic partnership", body: "A senior advisor who stays through delivery, accountable to the outcome, not just the advice." },
];

const OUTCOMES = [
  { title: "Business Growth", note: "New revenue, markets and capacity." },
  { title: "Operational Excellence", note: "A leaner, more resilient core." },
  { title: "Executive Visibility", note: "A clear board-level line of sight." },
  { title: "Investment Optimisation", note: "Spend tied to business return." },
  { title: "Improved Governance", note: "Decisions held to account." },
  { title: "Reduced Business Risk", note: "Cyber and delivery risk controlled." },
  { title: "Scalable Operations", note: "Systems built for the next stage." },
  { title: "Competitive Advantage", note: "Capability rivals cannot copy." },
];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Digital Transformation Advisory",
  serviceType: "Business & Digital Transformation Advisory",
  description: "Executive-led business transformation advisory. Aftab Azad works with leadership teams in growing businesses, mid-market companies and family-owned enterprises to ensure transformation initiatives support measurable business outcomes across India, the UAE and the Gulf Region.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/services/digital-transformation",
};

export default function DigitalTransformation() {
  return (
    <>
      <Seo
        title="Digital Transformation Advisory"
        description="Business transformation begins with better leadership decisions. Aftab Azad works with executive teams in growing businesses across India, the UAE and the Gulf Region to ensure transformation delivers measurable business outcomes, not new systems."
        path="/services/digital-transformation"
        keywords={["Digital Transformation Advisor", "Business Transformation Advisor", "Executive Technology Advisor", "Fractional CIO", "Technology Strategy Consultant", "Mid-Market Transformation", "Family-Owned Business Advisor"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="dt-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <StrategyNetwork className="pointer-events-none absolute inset-y-10 right-0 hidden h-[calc(100%-5rem)] w-[38%] max-w-[520px] pr-4 opacity-90 md:block lg:w-[40%]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">Digital Transformation</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["Business transformation begins", "with better leadership"]} lineClassName="font-light" />
            <KineticHeading lines={["decisions."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            Digital transformation is not about implementing new technology. It is about creating a
            business that is more efficient, more agile, more resilient and better prepared for future
            growth. Aftab Azad works with executive leadership teams to ensure transformation
            initiatives support measurable business outcomes rather than simply introducing new systems.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="dt-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Book Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/contact" data-testid="dt-hero-discuss"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Discuss Your Business Challenges
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHY INITIATIVES FAIL */}
      <section data-testid="dt-failures" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why many transformations fail</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Technology first,", "strategy second."]} />
            </h2>
            <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-slate-600">
              Most transformation initiatives fail for the same underlying reason: the organisation
              focuses on technology before business strategy. Successful transformation begins with
              leadership alignment.
            </p>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {FAILURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={(i % 3) * 0.06}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-xl font-bold leading-snug tracking-tight text-navy">{f.title}</h3>
                    <p className="mt-3 font-body leading-relaxed text-slate-600">{f.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — TRANSFORMATION SHOULD DELIVER BUSINESS OUTCOMES */}
      <section data-testid="dt-transformation-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Transformation should deliver</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["Business outcomes,", "not new systems."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 lg:grid-cols-5">
            {TRANSFORMATION_OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-base font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — TRANSFORMATION FRAMEWORK */}
      <section data-testid="dt-framework" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Our business transformation framework</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["From vision", "to result."]} />
            </h2>
          </div>
          <div className="mt-16 max-w-3xl">
            {FRAMEWORK.map((s, i) => (
              <Reveal key={s.title} className="relative flex gap-6 pb-10 last:pb-0">
                {i < FRAMEWORK.length - 1 && <span aria-hidden className="absolute left-6 top-14 h-[calc(100%-2.5rem)] w-px bg-slate-200" />}
                <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-navy font-heading text-lg font-bold text-white">{i + 1}</div>
                <div className="pt-1.5">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-2 font-body leading-relaxed text-slate-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — CHALLENGES WE HELP SOLVE */}
      <section data-testid="dt-challenges" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Transformation challenges we help solve</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Where growing", "businesses get stuck."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {CHALLENGES.map((c) => (
              <Reveal key={c.title} className="border-t border-slate-200 pt-6">
                <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{c.title}</h3>
                <p className="mt-2 max-w-md font-body leading-relaxed text-slate-600">{c.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — LEADERSHIP BEFORE TECHNOLOGY */}
      <section data-testid="dt-convictions" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Leadership before technology</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Convictions that", "shape every engagement."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-12 md:grid-cols-2">
            {CONVICTIONS.map((c, i) => (
              <Reveal key={c.title} className="border-t border-slate-200 pt-6">
                <span className="font-body text-sm font-bold text-royal">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy">{c.title}</h3>
                <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">{c.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHY WORK WITH AFTAB */}
      <section data-testid="dt-why" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why business leaders work with Aftab Azad</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The difference", "in the room."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-14 md:grid-cols-2">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} className="flex gap-6 border-t border-slate-200 pt-8">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{w.title}</h3>
                    <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 8 — BUSINESS OUTCOMES */}
      <section data-testid="dt-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -right-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 md:grid-cols-4">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-lg font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        overline="Ready to transform your business?"
        heading={["Let's build the plan", "for what's next."]}
        body="Whether your organisation is expanding, modernising operations, evaluating AI or planning its next phase of growth, let's discuss how strategic technology leadership can help you achieve meaningful business outcomes."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Explore Advisory Services", to: "/services/executive-advisory" }}
      />
    </>
  );
}
